/* eslint-disable no-use-before-define, @typescript-eslint/no-use-before-define */
import { cloneElement, createElement, ReactElement } from 'react';

import { CodeSegment } from '../types';

import { reduce } from './react-children-utils';

type LineWrapAccumulator = {
  lines: ReactElement[];
  childQueue: CodeSegment[];
};

export const LINE_BREAK = '\n';

const createLine = (
  children: CodeSegment[],
  parentProps = {}
): ReactElement => {
  const keyedChildren = children.map((c, i) =>
    // eslint-disable-next-line react/no-array-index-key
    typeof c === 'string' ? c : cloneElement(c, { key: i })
  );
  return createElement('span', parentProps, keyedChildren);
};

/*
 * A function for processing a single text node within the algorithm's main reducer.
 */
function processText(
  acc: LineWrapAccumulator,
  text: string,
  parentProps = {}
): LineWrapAccumulator {
  const { lines, childQueue } = acc;
  const segments = text.split(LINE_BREAK);

  // If the text node does not contain a newline, push it onto the queue to be
  // grouped into a line the next time a newline is encountered
  if (segments.length === 1) {
    childQueue.push(text);
  } else {
    segments.forEach((segment, i) => {
      const isFinalSegment = i === segments.length - 1;
      // Preserve the newline character at the ends of the split lines generated here
      const newText = isFinalSegment ? segment : `${segment}\n`;
      if (i === 0 && childQueue.length) {
        // Any queued elements need to be grouped with the current text segment
        // just before the first newline, and then the queue needs to be emptied
        lines.push(createLine([...childQueue, newText], parentProps));
        childQueue.length = 0;
      } else if (!isFinalSegment) {
        // This text segment exists between newlines, so it can safely be turned
        // into its own line (preserving the original containing element's props)
        lines.push(createLine([newText], parentProps));
      } else if (segment !== '') {
        // Queue the final segment to be grouped into a line the next time a newline
        // is encountered
        childQueue.push(newText);
      } else if (isFinalSegment && segment === '') {
        // Queue the segment when a new line is encountered and it is the final segment
        childQueue.push(newText);
      }
    });
  }

  return {
    lines,
    childQueue,
  };
}

/*
 * A function for processing a single React element within the algorithm's main reducer.
 */
function processElement(
  acc: LineWrapAccumulator,
  element: ReactElement,
  parentProps = {}
): LineWrapAccumulator {
  /*
   * Run the algorithm on the element in isolation to split it into lines, and
   * pass the element's props (ex: className) in order to preserve them on the
   * output lines
   *
   * Example:
   *
   * `<span className="foo">bar\nbaz</span>`
   *
   * will return:
   *
   * `{ lines: [<span className="foo">bar\n</span>], childQueue: ["baz"]}
   *
   */
  const results = wrapLines(element.props.children, element.props);

  const { lines } = results;
  let { childQueue } = results;

  // Queued elements need to be wrapped in a parent that retains their original
  // parent's props
  if (childQueue.length) {
    childQueue = [createLine([...childQueue], element.props)];
  }

  if (lines.length) {
    return {
      lines: [
        ...acc.lines,
        // The queue of elements passed into this step in the reducer should be prepended
        // to the first line of the results, and also needs to be wrapped in a parent
        // that retains their original parent's props
        createLine([...acc.childQueue, lines[0]], parentProps),
        // The remaining lines of the results also need to be wrapped in that manner
        ...lines.slice(1).map(l => createLine([l], parentProps)),
      ],
      childQueue,
    };
  }

  return {
    lines: [...acc.lines],
    childQueue: [...acc.childQueue, ...childQueue],
  };
}

/*
 * Uses a divide and conquer strategy for splitting React elements into lines
 * by recursively processing elements and their children until a text node is
 * reached. At this point, the contents can be split by newline characters.
 *
 * Keeps track of the lines that have been collected so far, and a queue of elements and
 * text nodes to group together into a line upon the next occurence of a newline.
 */
function wrapLines(
  elements: CodeSegment[],
  parentProps = {}
): LineWrapAccumulator {
  return reduce(
    elements,
    (acc: LineWrapAccumulator, element: CodeSegment) =>
      typeof element === 'string'
        ? processText(acc, element, parentProps)
        : processElement(acc, element, parentProps),
    {
      lines: [],
      childQueue: [],
    }
  );
}

/*
 * Groups a series of React children by line.
 *
 * Example:
 *
 * ```
 * $(<span class="token-keyword">function</span> () {\n  <span class="token-built_in">console</span>.log(<span class="token-string">'foo'</span>);\n}
 * ```
 *
 * will output the following:
 *
 * ```
 * <span>$(<span class="token-keyword">function</span> () {\n</span>
 * <span>  <span class="token-built_in">console</span>.log(<span class="token-string">'foo'</span>);\n</span>
 * <span>}</span>
 * <span />
 * ```
 */
export default function (elements: CodeSegment[]): ReactElement[] {
  const { lines, childQueue } = wrapLines(elements);
  if (childQueue.length) {
    lines.push(createLine(childQueue));
  }
  return lines;
}
